import type { FC, ReactNode } from 'react';
import React from 'react';
import { useDrag } from 'react-dnd';
import { FaDolly as DollyIcon } from 'react-icons/fa';
import ArchitectureRoundedIcon from '@mui/icons-material/ArchitectureRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { Box, MenuItem, Stack, Typography } from '@mui/material';

import { ReportOrderStatus } from 'src/constants/report';
import type { TemplateOrReportItem } from 'src/types/workspace';

import { styles } from './index.styles';

export interface CustomItemProps {
  children: ReactNode;
  dragSourceType: string;
  id: string | number;
  isFavourite: boolean;
  isReadOnly: boolean;
  item: TemplateOrReportItem;
  orderStatus: ReportOrderStatus;
  title: string;
}

const CustomItem: FC<CustomItemProps> = ({
  children,
  dragSourceType,
  id,
  isFavourite,
  isReadOnly,
  item,
  orderStatus,
  title,
}) => {
  const [, drag] = useDrag({
    type: dragSourceType,
    item: { dragSourceType, id, item, title },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <MenuItem ref={drag} sx={styles.listItem}>
      <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
        <Stack direction="row" sx={styles.statusIcon}>
          {isFavourite && <StarRoundedIcon color="primary" sx={styles.icon} />}
          {orderStatus === ReportOrderStatus.DRAFT && (
            <ArchitectureRoundedIcon sx={styles.icon} />
          )}
          {orderStatus === ReportOrderStatus.ORDERED && (
            <Box sx={styles.dolly}>
              <DollyIcon data-test-id="DollyIcon" />
            </Box>
          )}
          {isReadOnly && (
            <LockRoundedIcon
              color="tertiary"
              fontSize="small"
              sx={styles.icon}
            />
          )}
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <Typography>{title}</Typography>
          <Box>{children}</Box>
        </Stack>
      </Stack>
    </MenuItem>
  );
};

export default CustomItem;
